/**
 * The DOM surface a Turnstile mint renders its widget into.
 *
 * Cloudflare decides per mint whether a visitor is waved through silently or escalated to an
 * interactive challenge, and what it renders is a bare ~300x65 iframe with no chrome of its own.
 * Anchored in a page corner, an escalated challenge is easy to miss entirely: the visitor sits in
 * front of an apparently idle form — a public claim lodgement upload, say — until the mint times
 * out and the request fails for no visible reason. So the surface has two states:
 *
 *   silent      — a pointer-transparent corner anchor. `appearance: 'interaction-only'` keeps the
 *                 widget itself invisible and nothing else is shown, so an ordinary mint stays
 *                 completely quiet.
 *   interactive — a centred card over a dimmed backdrop, captioned so the visitor knows what is
 *                 being asked and where to click.
 *
 * Both states are the same nodes restyled in place. Re-parenting or recreating the widget element
 * would reload Cloudflare's iframe and discard the challenge already in progress, so the state
 * switch only ever touches classes and attributes.
 */

// Ant Design overlays start at z-index 1000, so use the established 1100 overlay layer to keep an
// escalated challenge above a public-form modal.
const SILENT_CONTAINER_CLASS = 'pointer-events-none fixed right-4 bottom-4 z-[1100]';
const INTERACTIVE_CONTAINER_CLASS = 'fixed inset-0 z-[1100] flex justify-center overflow-y-auto bg-black/50 p-3 sm:p-4';

// `pointer-events-auto` re-enables clicks the container turns off while silent: the anchor must
// never swallow a click aimed at the page behind it, but if Cloudflare ever shows widget UI
// without announcing it through `before-interactive-callback`, that UI must stay completable.
const SILENT_PANEL_CLASS = 'pointer-events-auto';
// `outline-none`: the panel is a focus target only so the challenge can be reached by keyboard,
// not an interactive control of its own — a focus ring around the whole card just reads as a
// stray blue border.
const INTERACTIVE_PANEL_CLASS =
    'my-auto flex min-w-0 max-w-sm flex-col items-center gap-4 rounded-xl border border-separator bg-card p-4 text-center shadow-overlay outline-none sm:p-6';

const CAPTION_TITLE = "Verify you're human";
const CAPTION_BODY = 'Complete the check below to continue.';
const CAPTION_TITLE_ID = 'turnstile-challenge-title';

/**
 * Cloudflare's `normal` widget is a fixed 300px wide. Below this viewport the modal's padding no
 * longer leaves room for it and the iframe would overflow its card, so the `compact` widget
 * (150px) takes over — it still fits the narrowest phones in use.
 */
const COMPACT_WIDGET_MAX_VIEWPORT_PX = 360;

let containerEl: HTMLDivElement | null = null;
let panelEl: HTMLDivElement | null = null;
let captionEl: HTMLDivElement | null = null;
let widgetEl: HTMLDivElement | null = null;
let restoreFocusTo: HTMLElement | null = null;
const inertedBodyChildren = new Set<HTMLElement>();

/**
 * The widget size to render at this viewport. Decided once at render time — Cloudflare only
 * reveals whether the mint escalates afterwards, so the choice has to hold for both states.
 */
export function challengeWidgetSize(): 'compact' | 'normal' {
    return window.innerWidth > 0 && window.innerWidth < COMPACT_WIDGET_MAX_VIEWPORT_PX ? 'compact' : 'normal';
}

/** Mount (or reuse) the surface and return the element Turnstile should render into. */
export function ensureChallengeSurface(): HTMLDivElement {
    if (!containerEl || !document.body.contains(containerEl)) {
        containerEl = document.createElement('div');
        containerEl.setAttribute('role', 'status');
        containerEl.setAttribute('aria-live', 'polite');

        panelEl = document.createElement('div');
        // Focus target for the interactive state: a keyboard or screen-reader visitor lands on
        // the challenge instead of hunting for an iframe that appeared without warning.
        panelEl.setAttribute('tabindex', '-1');
        containerEl.appendChild(panelEl);

        captionEl = document.createElement('div');
        captionEl.dataset.turnstileCaption = '';
        const title = document.createElement('p');
        title.id = CAPTION_TITLE_ID;
        title.className = 'font-semibold text-base text-foreground';
        title.textContent = CAPTION_TITLE;
        const body = document.createElement('p');
        body.className = 'mt-1 text-muted-foreground text-sm';
        body.textContent = CAPTION_BODY;
        captionEl.append(title, body);
        panelEl.appendChild(captionEl);

        widgetEl = document.createElement('div');
        // The widget is a fixed-width iframe sized at render time. A viewport that narrows
        // afterwards (a phone rotated to portrait mid-challenge) must scroll it inside the card
        // rather than push the whole public form sideways.
        widgetEl.className = 'max-w-full overflow-x-auto';
        panelEl.appendChild(widgetEl);

        document.body.appendChild(containerEl);
        setChallengeInteractive(false);
    }

    return widgetEl!;
}

/**
 * Switch the mounted surface between its silent and interactive presentations.
 *
 * Safe to call before the surface exists (a mint that never rendered) and safe to call
 * repeatedly — Cloudflare can escalate and de-escalate within a single mint.
 */
export function setChallengeInteractive(interactive: boolean): void {
    if (!containerEl || !panelEl || !captionEl) return;

    const wasInteractive = containerEl.dataset.turnstileSurface === 'interactive';
    containerEl.className = interactive ? INTERACTIVE_CONTAINER_CLASS : SILENT_CONTAINER_CLASS;
    containerEl.dataset.turnstileSurface = interactive ? 'interactive' : 'silent';
    // An escalation interrupts the visitor mid-task, so it is announced rather than queued
    // behind whatever else the page is saying.
    containerEl.setAttribute('aria-live', interactive ? 'assertive' : 'polite');
    panelEl.className = interactive ? INTERACTIVE_PANEL_CLASS : SILENT_PANEL_CLASS;
    captionEl.classList.toggle('hidden', !interactive);
    if (interactive) {
        panelEl.setAttribute('role', 'dialog');
        panelEl.setAttribute('aria-modal', 'true');
        panelEl.setAttribute('aria-labelledby', CAPTION_TITLE_ID);
    } else {
        removeDialogSemantics();
    }

    if (interactive && !wasInteractive) {
        if (document.activeElement instanceof HTMLElement && !containerEl.contains(document.activeElement)) {
            restoreFocusTo = document.activeElement;
        }
        makeBackgroundInert();
        document.addEventListener('focusin', containChallengeFocus);
        // preventScroll: the page behind the backdrop must not jump — the visitor keeps their
        // place in the form once the challenge is done.
        panelEl.focus({ preventScroll: true });
    } else if (!interactive && wasInteractive) {
        document.removeEventListener('focusin', containChallengeFocus);
        restoreBackgroundInteractivity();
        returnFocus();
    }
}

/** Keep pointer and keyboard interaction in the modal while Cloudflare's challenge is visible. */
function makeBackgroundInert(): void {
    for (const child of document.body.children) {
        if (!(child instanceof HTMLElement) || child === containerEl || child.inert) continue;
        child.inert = true;
        inertedBodyChildren.add(child);
    }
}

function restoreBackgroundInteractivity(): void {
    for (const child of inertedBodyChildren) {
        child.inert = false;
    }
    inertedBodyChildren.clear();
}

/**
 * `inert` provides the browser-level Tab boundary. This guard also contains programmatic focus
 * if page code attempts to move it while the challenge is blocking interaction.
 */
function containChallengeFocus(event: FocusEvent): void {
    if (!(event.target instanceof Node) || containerEl?.contains(event.target) === true) return;
    panelEl?.focus({ preventScroll: true });
}

function removeDialogSemantics(): void {
    panelEl?.removeAttribute('role');
    panelEl?.removeAttribute('aria-modal');
    panelEl?.removeAttribute('aria-labelledby');
}

/**
 * Hand focus back to whatever the visitor was on before the challenge interrupted them, so a
 * completed challenge does not dump them at the top of the document.
 */
function returnFocus(): void {
    const target = restoreFocusTo;
    restoreFocusTo = null;
    if (!target?.isConnected) return;

    // Reclaim focus only if the challenge still holds it — or has just dropped it. Cloudflare's
    // widget is torn down before this runs, and destroying the focused iframe resets
    // `activeElement` to `<body>`, so "the challenge had focus" is the common case rather than
    // the exception. If the visitor has focused something else in the meantime, leave them there.
    const active = document.activeElement;
    const challengeHeldFocus = active === null || active === document.body || containerEl?.contains(active) === true;
    if (!challengeHeldFocus) return;

    target.focus({ preventScroll: true });
}

/** Tear the surface down once the mint has settled. */
export function removeChallengeSurface(): void {
    document.removeEventListener('focusin', containChallengeFocus);
    removeDialogSemantics();
    restoreBackgroundInteractivity();
    returnFocus();
    containerEl?.remove();
    containerEl = null;
    panelEl = null;
    captionEl = null;
    widgetEl = null;
}
